import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CommonsText } from 'app/commons/CommonsText';
import { EventService } from 'app/services/event-service';
import { ToastrService } from 'ngx-toastr';

type DetailTab = 'detail' | 'messenger';
type AccordionKey = 'pagos' | 'servicios' | 'indemnizaciones' | 'comunicaciones' | 'documentos';

@Component({
    selector: 'app-event-detail-page',
    templateUrl: './event-detail-page.component.html',
    styleUrls: ['./event-detail-page.component.scss']
})
export class EventDetailPageComponent implements OnInit {

    event: any = undefined;
    eventNumber: any;
    loadingEvent: Boolean = true;

    selectedTab: DetailTab = 'detail';

    infoText: string = 'Envía tus consultas o documentación a tu tramitador/a desde este espacio. Recibirás respuesta en un plazo máximo de 24 horas';

    accordions: { [key in AccordionKey]: boolean } = {
        pagos: false,
        servicios: false,
        indemnizaciones: false,
        comunicaciones: false,
        documentos: false
    };

    constructor(private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private eventService: EventService,
        private toastr: ToastrService) { }

    ngOnInit() {
        this.eventNumber = this.route.snapshot.params['eventNumber'];

        const show = this.route.snapshot.params['show'];
        if (show === 'messenger') {
            this.selectedTab = 'messenger';
        }

        this.eventService.getEventByNumber(this.eventNumber)
            .then(success => {
                this.loadingEvent = false;
                if (success.response.error === 'invalid.xml') {
                    this.toastr.error(CommonsText.PROBLEMA_DATOS_PARTE, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                        timeOut: 5000
                    });
                } else {
                    this.event = success.response;
                }

            }).catch(error => {
                this.loadingEvent = false;
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_PARTES, CommonsText.PROBLEMA_DE_CONECTIVIDAD, {
                    timeOut: 5000
                });
            });
    }

    selectTab(tab: DetailTab): void {
        this.selectedTab = tab;
    }

    toggleAccordion(key: AccordionKey): void {
        this.accordions[key] = !this.accordions[key];
    }

    goBack(): void {
        this.location.back();
    }

    irChat(): void {
        this.router.navigate(['/contact/form']);
    }

    /**
     * Mapea event.eventStatus al paso del progress bar de 3 tramos.
     * 1 = Pendiente de referencia · 2 = En trámite · 3 = Cerrado
     */
    getStep(): number {
        if (!this.event) { return 1; }
        switch (this.event.eventStatus) {
            case 'CERR':
            case 'EXPI':
                return 3;
            case 'PDTE':
            case 'ABIE':
                return 2;
            case 'GRAB':
            default:
                return 1;
        }
    }

    /** Ultimo tramo del progress bar ("Cerrado"): es un estado final, no "en curso". */
    private static readonly LAST_STEP = 3;

    isStepActive(step: number): boolean {
        // Cerrado no se pinta como activo (medio tramo + punto) sino como completado
        return this.getStep() === step && step !== EventDetailPageComponent.LAST_STEP;
    }

    isStepDone(step: number): boolean {
        // Con el expediente cerrado la barra se rellena entera, incluido el ultimo tramo
        return this.getStep() > step
            || (step === EventDetailPageComponent.LAST_STEP && this.getStep() === EventDetailPageComponent.LAST_STEP);
    }

    /** Devuelve el estado del expediente (event.status) con el orden indicado. */
    private statusByOrder(order: number): any {
        if (!this.event || !this.event.status) { return undefined; }
        return this.event.status.find((s: any) => s.order === order);
    }

    /**
     * Fecha en la que el expediente entró en cada uno de los 3 tramos del progress bar.
     * Mismo mapeo que el antiguo checkballs sobre event.status (order 1 = GRAB, 2 = ABIE,
     * 3 = PDTE, 4 = CERR):
     *   · Paso 1 "Pendiente de referencia" → order 1 (o fecha de declaración si no tiene).
     *   · Paso 2 "En trámite"               → order 3 (o, en su defecto, order 2).
     *   · Paso 3 "Cerrado"                  → order 4.
     */
    stepDate(step: number): any {
        if (!this.event) { return null; }
        switch (step) {
            case 1: {
                const s1 = this.statusByOrder(1);
                return (s1 && s1.date) ? s1.date : this.event.eventStartDate;
            }
            case 2: {
                const s3 = this.statusByOrder(3);
                if (s3 && s3.date) { return s3.date; }
                const s2 = this.statusByOrder(2);
                return s2 ? s2.date : null;
            }
            case 3: {
                const s4 = this.statusByOrder(4);
                return s4 ? s4.date : null;
            }
            default:
                return null;
        }
    }

    /** Clasifica la situación del expediente para el badge de la tabla de garantías. */
    badgeClass(situacion: string): string {
        const s = (situacion || '').toLowerCase();
        if (s.indexOf('finaliz') !== -1 || s.indexOf('cerrad') !== -1) {
            return 'badge--finalizado';
        }
        return 'badge--info';
    }

}
